import React from 'react';
import { AdaptiveModal } from '../../components/AdaptiveModal';
import TodoForm from '../../components/TodoForm';
import { TeamModal } from '../../components/TeamModal';
import { JoinTeamModal } from '../../components/JoinTeamModal';
import { PomodoroModal } from '../../components/PomodoroModal';
import { EventModal } from '../../components/EventModal';
import { AIChatModal } from '../../components/ai/AIChatModal';
import { Todo, TodoCreate, TeamWithMembers, User, TeamMember, Event } from '../../types';
import type { TodoFormRef } from '../../components/TodoForm';

interface AppModalsProps {
  // Todo Form Modal
  showTodoForm: boolean;
  selectedTodo: Todo | null;
  todoFormRef: React.RefObject<TodoFormRef>;
  allTags: string[];
  allTodos: Todo[];
  addingChildToParentId: string | null;
  initialDueDate: string | null;
  initialTodoDate: string | null;
  initialActiveInWeek: boolean;
  initialActiveInMonth: boolean;
  selectedTeamId: string | null;
  currentUser: User | undefined;
  teamMembers: TeamMember[];
  isPersonalMode: boolean;
  isCreatingTodo: boolean;
  isUpdatingTodo: boolean;
  onCreateTodo: (todoData: TodoCreate) => void;
  onUpdateTodo: (data: TodoCreate) => void;
  onAutoSaveTodo: (data: TodoCreate) => void;
  onCloseTodoModal: () => void;
  onCancelTodoModal: () => void;
  onDeleteTodo: (todoId: string) => void;
  onCloneTodo: (todoId: string) => Promise<void>;
  onSubtaskClick?: (todo: Todo) => void;
  onParentClick?: (todo: Todo) => void;

  // Team Modals
  showTeamModal: boolean;
  editingTeam: TeamWithMembers | null;
  showJoinTeamModal: boolean;
  onCloseTeamModal: () => void;
  onCloseJoinTeamModal: () => void;

  // Pomodoro Timer Modal
  showPomodoroTimer: boolean;
  currentPomodoroTask: Todo | null;
  onClosePomodoroTimer: () => void;
  setCurrentPomodoroTask: (task: Todo | null) => void;

  // AI Assistant Modal
  showAIAssistant: boolean;
  onCloseAIAssistant: () => void;

  // Event Scheduling Modal
  scheduleTodo?: Todo | null;
  editEvent?: Event | null;
  onCloseEventModal?: () => void;
  onCloseEditEvent?: () => void;
  onScheduleTodoClick?: (todo: Todo) => void;
}

export function AppModals({
  // Todo Form Modal props
  showTodoForm,
  selectedTodo,
  todoFormRef,
  allTags,
  allTodos,
  addingChildToParentId,
  initialDueDate,
  initialTodoDate,
  initialActiveInWeek,
  initialActiveInMonth,
  selectedTeamId,
  currentUser,
  teamMembers,
  isPersonalMode,
  isCreatingTodo,
  isUpdatingTodo,
  onCreateTodo,
  onUpdateTodo,
  onAutoSaveTodo,
  onCloseTodoModal,
  onCancelTodoModal,
  onDeleteTodo,
  onCloneTodo,
  onSubtaskClick,
  onParentClick,

  // Team Modal props
  showTeamModal,
  editingTeam,
  showJoinTeamModal,
  onCloseTeamModal,
  onCloseJoinTeamModal,

  // Pomodoro Timer Modal props
  showPomodoroTimer,
  currentPomodoroTask,
  onClosePomodoroTimer,
  setCurrentPomodoroTask,

  // AI Assistant Modal props
  showAIAssistant,
  onCloseAIAssistant,

  // Event Scheduling Modal props
  scheduleTodo,
  editEvent,
  onCloseEventModal,
  onCloseEditEvent,
  onScheduleTodoClick
}: AppModalsProps) {
  return (
    <>
      {/* Todo Form Modal */}
      <AdaptiveModal
        isOpen={showTodoForm}
        onClose={() => {
          onCancelTodoModal();
        }}
        title=""
        size="lg"
      >
        <TodoForm
          availableTags={allTags}
          allTodos={allTodos}
          initialParentId={addingChildToParentId || undefined}
          initialDueDate={initialDueDate || undefined}
          initialTodoDate={initialTodoDate || undefined}
          initialActiveInWeek={initialActiveInWeek}
          initialActiveInMonth={initialActiveInMonth}
          initialTeamId={selectedTeamId || undefined}
          currentUser={currentUser}
          teamMembers={teamMembers}
          isPersonalMode={isPersonalMode}
          onSubmit={onCreateTodo}
          onCancel={onCancelTodoModal}
          isLoading={isCreatingTodo}
          ref={todoFormRef}
        />
      </AdaptiveModal>

      {/* Todo Detail Modal */}
      <AdaptiveModal
        isOpen={!!selectedTodo}
        onClose={onCloseTodoModal}
        title=""
        size="lg"
      >
        {selectedTodo && (
          <TodoForm
            todo={selectedTodo}
            availableTags={allTags}
            allTodos={allTodos}
            onSubmit={onUpdateTodo}
            onCancel={onCancelTodoModal}
            onAutoSave={onAutoSaveTodo}
            onDelete={onDeleteTodo}
            onClone={onCloneTodo}
            onSubtaskClick={onSubtaskClick}
            onParentClick={onParentClick}
            isLoading={isUpdatingTodo}
            currentUser={currentUser}
            teamMembers={teamMembers}
            isPersonalMode={isPersonalMode}
            ref={todoFormRef}
          />
        )}
      </AdaptiveModal>

      {/* Team Modal */}
      {showTeamModal && (
        <TeamModal
          isOpen={showTeamModal}
          onClose={onCloseTeamModal}
          team={editingTeam}
        />
      )}

      {/* Join Team Modal */}
      {showJoinTeamModal && (
        <JoinTeamModal
          isOpen={showJoinTeamModal}
          onClose={onCloseJoinTeamModal}
        />
      )}

      {/* Pomodoro Timer Modal */}
      {showPomodoroTimer && (
        <PomodoroModal
          isOpen={showPomodoroTimer}
          onClose={onClosePomodoroTimer}
          task={currentPomodoroTask}
          selectedTeamId={selectedTeamId}
          onTaskComplete={() => setCurrentPomodoroTask(null)}
        />
      )}

      {/* AI Assistant Modal */}
      {showAIAssistant && (
        <AIChatModal
          isOpen={showAIAssistant}
          onClose={onCloseAIAssistant}
        />
      )}

      {/* Event Scheduling / Edit Modal */}
      {(scheduleTodo || editEvent) && onCloseEventModal && (
        <EventModal
          isOpen={!!(scheduleTodo || editEvent)}
          onClose={() => { onCloseEventModal?.(); onCloseEditEvent?.(); }}
          event={editEvent || undefined}
          linkedTodo={scheduleTodo || undefined}
          onLinkedTodoClick={onScheduleTodoClick}
        />
      )}
    </>
  );
}
